import { ListingMatch } from '@deltek/specification-client/core/Models/listingSearch'
import { createDevStore } from 'bpm/shared/utils/devtools'
import { createIndexedDBStorage } from 'components/WorkspaceEditor/persistDb'
import { StateCreator, create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export type PSEInfoDialogType = {
    isExpanded: boolean
    setIsExpanded: (isExpanded: boolean) => void
}

const store = createDevStore(
    persist(
        (set) => ({
            isExpanded: true,
            setIsExpanded: (isExpanded: boolean) => set({ isExpanded }),
        }),
        {
            name: 'pse-info-dialog',
            storage: createJSONStorage(() => localStorage),
        }
    ) as StateCreator<PSEInfoDialogType, [], [['zustand/persist', unknown]]>,
    'PSE-INFO-DIALOG'
)

export const usePSEInfoDialogStore = create(store)

export type ProductListingDialogStoreType = {
    productListings: Record<string, Partial<ListingMatch>>
    setProductListings: (listings: Partial<ListingMatch>[]) => void
    checkStaleListings: (
        minimalListings: Array<{ listingId: string; lastUpdated: string }>
    ) => string[]
}

export const useProductListingDialogStore = create<
    ProductListingDialogStoreType
>()(
    persist(
        (set, get) => ({
            productListings: {},
            setProductListings: (listings) =>
                set((state) => {
                    const normalizedListings = listings.reduce(
                        (
                            acc: Record<string, Partial<ListingMatch>>,
                            listing: Partial<ListingMatch>
                        ) => {
                            return { ...acc, [listing.listingId!]: listing }
                        },
                        {}
                    )

                    return {
                        productListings: {
                            ...state.productListings,
                            ...normalizedListings,
                        },
                    }
                }),

            // Check which listings are missing or outdated
            checkStaleListings: (minimalListings) => {
                const state = get()
                const listingsToFetch: string[] = []

                minimalListings.forEach(({ listingId, lastUpdated }) => {
                    const cached = state.productListings[listingId]

                    // Not in cache OR cache is outdated
                    if (!cached || cached.lastUpdated !== lastUpdated) {
                        listingsToFetch.push(listingId)
                    }
                })

                return listingsToFetch
            },
        }),
        {
            name: 'product-listings-store',
            storage: createIndexedDBStorage(
                'product-listings-db',
                'product-listings-store'
            ),
            partialize: (state) => ({
                productListings: state.productListings,
            }),
        }
    )
)
